import {
    BackendStyleOptionsResponse,
    StyleOptions,
} from '@shared/features/livechat/types/StyleOptions';
import { MessagingChannelsIntegrations } from '@shared/types/channels.types';

export const LIVECHAT_SOCIAL_CHANNELS = {
    INSTAGRAM: 'instagram',
    MESSENGER: 'messenger',
    WHATSAPP: 'whatsapp',
} as const;
export type LivechatSocialChannels =
    (typeof LIVECHAT_SOCIAL_CHANNELS)[keyof typeof LIVECHAT_SOCIAL_CHANNELS];

export type LivechatSocialChannelsRecord = Record<
    LivechatSocialChannels,
    {
        url?: string;
        whatsappweb_account_id?: string;
        whatsappcloud_account_id?: string;
        ig_account?: string;
        page_id?: string;
    }
>;
export type LivechatSocialChannelsPartial = Partial<LivechatSocialChannelsRecord>;

export type LivechatSettings = {
    enabled: boolean;
    preferences: LivechatPrefs;
};

export type LivechatPrefs = {
    style_options?: BackendStyleOptionsResponse;
    avatar?: string;
    chat_icon_avatar?: string;
    channels?: LivechatSocialChannelsPartial;
    languages: LivechatLanguage[];
};

export type LivechatLanguage = {
    code: string; // 2-character language code . Ex: 'en', 'es'
    default?: boolean;
    footer_message?: string;
    header_message?: string;
    initial_message?: string;
    placeholder?: string;
    policy?: string;
};

export type PreferencesChannels = {
    [key in MessagingChannelsIntegrations]?: {
        url?: string;
        whatsappweb_account_id?: string;
        whatsappcloud_account_id?: string;
        ig_account?: string;
        page_id?: string;
    };
};

type PreferencesConfig = {
    styleOptions?: StyleOptions;
    avatar?: string;
    chatIconAvatar?: string;
    avatarFile?: File;
    chatIconAvatarFile?: File;
    channels?: PreferencesChannels;
    languages: LivechatLanguageUI[];
};

export type LivechatLanguageUI = {
    code: string;
    collapsed: boolean;
    default: boolean;
    footerMessage?: string;
    headerMessage?: string;
    initialMessage?: string;
    name?: string;
    placeholder?: string;
    policy?: string;
};

export class LivechatPrefsUI {
    avatar?: string;
    avatarFile?: File;
    channels: PreferencesChannels;
    chatIconAvatar?: string;
    chatIconAvatarFile?: File;
    languages: LivechatLanguageUI[];
    styleOptions?: StyleOptions;

    constructor(config: PreferencesConfig) {
        this.avatar = config.avatar;
        this.avatarFile = config.avatarFile;
        this.channels = config.channels ?? {};
        this.chatIconAvatar = config.chatIconAvatar;
        this.chatIconAvatarFile = config.chatIconAvatarFile;
        this.languages = config.languages;
        this.styleOptions = config.styleOptions;
    }

    public static fromBackendResponse(backendData: LivechatPrefs): LivechatPrefsUI {
        return new LivechatPrefsUI({
            avatar: backendData.avatar ?? undefined,
            avatarFile: undefined,
            channels: LivechatPrefsUI.toPreferencesChannels(backendData.channels ?? {}),
            chatIconAvatar: backendData.chat_icon_avatar ?? undefined,
            chatIconAvatarFile: undefined,
            languages: LivechatPrefsUI.fromBackendLanguages(backendData.languages),
            styleOptions: backendData.style_options
                ? StyleOptions.fromBackendResponse(backendData.style_options)
                : undefined,
        });
    }

    public static toBackendRequest(preferencesOptions: LivechatPrefsUI): LivechatPrefs {
        return {
            avatar: preferencesOptions.avatar,
            channels: LivechatPrefsUI.toBackendChannels(preferencesOptions.channels),
            chat_icon_avatar: preferencesOptions.chatIconAvatar,
            languages: LivechatPrefsUI.toBackendLanguages(preferencesOptions.languages),
            style_options: preferencesOptions.styleOptions
                ? StyleOptions.toBackendRequest(preferencesOptions.styleOptions)
                : undefined,
        };
    }

    private static toPreferencesChannels(
        channels: LivechatSocialChannelsPartial
    ): PreferencesChannels {
        const entries = Object.entries(channels).map(([key, value]) => {
            const hasWhatssappCloud = value?.whatsappcloud_account_id;
            const hasWhatsappWeb = value?.whatsappweb_account_id;

            let upperKey = key.toUpperCase() as MessagingChannelsIntegrations;
            if (hasWhatssappCloud) {
                upperKey = 'WHATSAPP_CLOUD';
            } else if (hasWhatsappWeb) {
                upperKey = 'WHATSAPP_WEB';
            }

            return [upperKey, value];
        });
        const result = Object.fromEntries(entries);
        return result;
    }

    private static toBackendChannels(channels: PreferencesChannels): LivechatSocialChannelsPartial {
        const entries = Object.entries(channels).map(([key, value]) => {
            const hasWhatssappCloud = value?.whatsappcloud_account_id;
            const hasWhatsappWeb = value?.whatsappweb_account_id;

            let lowerKey = key.toLowerCase() as LivechatSocialChannels;
            if (hasWhatssappCloud || hasWhatsappWeb) {
                lowerKey = 'whatsapp';
            }

            return [lowerKey, value];
        });
        const result = Object.fromEntries(entries);

        return result;
    }

    private static toBackendLanguages(languages: LivechatLanguageUI[]): LivechatLanguage[] {
        return languages.map(config => ({
            code: config.code,
            default: config.default,
            initial_message: config.initialMessage,
            placeholder: config.placeholder,
            footer_message: config.footerMessage,
            header_message: config.headerMessage,
            policy: config.policy,
        }));
    }

    private static fromBackendLanguages(languages: LivechatLanguage[]): LivechatLanguageUI[] {
        return languages.map(config => ({
            code: config.code,
            collapsed: true,
            default: config.default ?? false,
            footerMessage: config.footer_message,
            headerMessage: config.header_message,
            initialMessage: config.initial_message,
            placeholder: config.placeholder,
            policy: config.policy,
        }));
    }
}
